'use client';

import { useCallback, useState } from 'react';

import {
  EMPTY_HIGHLIGHT_DRAFT,
  isHighlightDraftDirty,
  type HighlightDraft
} from '../utils/highlight-draft';

interface DraftHistory {
  past: HighlightDraft[];
  present: HighlightDraft;
  future: HighlightDraft[];
}

const INITIAL: DraftHistory = {
  past: [],
  present: EMPTY_HIGHLIGHT_DRAFT,
  future: []
};

/**
 * Undo/redo over the unsaved highlight edits (OMA-593).
 *
 * Snapshot history rather than an inverse-operation log: a draft is a small
 * immutable value, and "undo a move that replaced an earlier move of the same
 * box" has no clean inverse to record. Every mutation pushes the previous
 * snapshot onto `past` and clears `future`, which is the standard rule — a new
 * edit after undoing abandons the redone branch.
 */
export function useHighlightDraft() {
  const [history, setHistory] = useState<DraftHistory>(INITIAL);

  const apply = useCallback(
    (mutate: (draft: HighlightDraft) => HighlightDraft) => {
      setHistory((prev) => {
        const next = mutate(prev.present);
        // A no-op mutation must not spend an undo step.
        if (next === prev.present) return prev;
        return {
          past: [...prev.past, prev.present],
          present: next,
          future: []
        };
      });
    },
    []
  );

  const undo = useCallback(() => {
    setHistory((prev) => {
      const previous = prev.past.at(-1);
      if (previous === undefined) return prev;
      return {
        past: prev.past.slice(0, -1),
        present: previous,
        future: [prev.present, ...prev.future]
      };
    });
  }, []);

  const redo = useCallback(() => {
    setHistory((prev) => {
      const [next, ...rest] = prev.future;
      if (next === undefined) return prev;
      return {
        past: [...prev.past, prev.present],
        present: next,
        future: rest
      };
    });
  }, []);

  // Used both by Cancel and after a successful save — the saved state IS the
  // new baseline, so the history is gone rather than rewindable into requests
  // that have already been sent.
  const reset = useCallback(() => setHistory(INITIAL), []);

  return {
    draft: history.present,
    apply,
    undo,
    redo,
    reset,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    isDirty: isHighlightDraftDirty(history.present)
  };
}
